import { useMemo, useState } from "react";
import {
	useTanstackTable,
	createColumnHelper,
	EMPTY_ARRAY,
} from "../../../hooks/useTanstackTable";
import { useApi } from "../../../hooks/useApi";
import type { Factor } from "../../../@types/Api_Extracted";
import { Button } from "../../ui/button";
import { DataTable } from "../../ui/datatable";
import { visualizarGramos } from "../../../lib/utils";
import { Dialog } from "../../ui/dialog";
import { DialogFactor } from "./FactorModal";
import { toast } from "react-toastify";

export function FactoresTable() {
	const [shownFactor, setShownFactor] = useState<Factor | null>(null);

	const api = useApi();
	const queryFactores = api.useQuery("get", "/v1/Empresa/Config/Factores", {
		params: {
			query: {
				tipo: "propias",
			},
		},
	});
	const queryBloques = api.useQuery("get", "/v1/Empresa/Config/Bloques");

	const handleNewInput = () => {
		setShownFactor({
			idFactor: crypto.randomUUID(),
			tipoFactor: "propio",
			idBloque: 0,
			nombre: "",
			unidad: "",
			conversion_equivalente_kilos: 0,
			conversion_co2_kg: 0,
			conversion_ch4_gramos: 0,
			conversion_n2o_gramos: 0,
		});
	};
	const onOpenChange = (open: boolean) => {
		if (!open) {
			setShownFactor(null);
			queryFactores.refetch();
		}
	};

	const columns = useMemo(() => Columns(setShownFactor), []);
	const table = useTanstackTable({
		columns,
		data: queryFactores.data ?? EMPTY_ARRAY,
	});

	return (
		<>
			<DataTable
				table={table}
				noResultsMessage="No hay factores de emisión personalizados"
				errorMessage="Error al cargar los factores de emisión personalizados"
				isLoading={queryFactores.isPending}
				isError={queryFactores.isError}
			/>
			<Dialog open={shownFactor !== null} onOpenChange={onOpenChange}>
				<DialogFactor
					key={shownFactor?.idFactor ?? "new-factor-modal"}
					factor={shownFactor}
					bloques={queryBloques.data ?? EMPTY_ARRAY}
					onSuccess={() => {
						setShownFactor(null);
						toast("Factor creado / editado.", { type: "success" });
						queryFactores.refetch();
					}}
				/>
			</Dialog>
			<Button
				variant="primary"
				className="float-right mt-4"
				onClick={handleNewInput}
			>
				Nuevo
			</Button>
		</>
	);
}

function Columns(setShownFactor: (factor: Factor | null) => void) {
	const columnHelper = createColumnHelper<Factor>();

	return [
		columnHelper.accessor("idBloque", {
			header: "Bloque",
			cell: (props) => props.getValue(),
		}),
		columnHelper.accessor("nombre", {
			header: "Nombre",
			cell: (props) => props.getValue(),
		}),
		columnHelper.accessor("unidad", {
			header: "Unidad",
			cell: (props) => props.getValue(),
		}),

		columnHelper.accessor("conversion_equivalente_kilos", {
			header: "Conversión Equivalente (kg)",
			cell: (props) => visualizarGramos(props.getValue() * 1000),
		}),
		columnHelper.accessor("conversion_co2_kg", {
			header: "Conversión CO2",
			cell: (props) => visualizarGramos(props.getValue() * 1000),
		}),
		columnHelper.accessor("conversion_ch4_gramos", {
			header: "Conversión CH4",
			cell: (props) => visualizarGramos(props.getValue()),
		}),
		columnHelper.accessor("conversion_n2o_gramos", {
			header: "Conversión N2O",
			cell: (props) => visualizarGramos(props.getValue()),
		}),

		columnHelper.display({
			id: "acciones",
			header: "Acciones",
			cell: (props) => (
				<AccionesFila
					fila={props.row.original}
					setShownFactor={setShownFactor}
				/>
			),
		}),
	];
}

function AccionesFila(props: {
	fila: Factor;
	setShownFactor: (factor: Factor | null) => void;
}) {
	return (
		<div className="grid grid-cols-1 gap-2">
			<Button
				variant="secondary"
				onClick={() => props.setShownFactor(props.fila)}
			>
				Editar
			</Button>
		</div>
	);
}
